import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Radio,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Base_URL, ApiCall } from "../Common/Path";
import customFailAlert from "../Alerts/customFailAlert";

const EditMemberPermissionsModal = ({
  open,
  onClose,
  member,
  household,
  onPermissionsUpdated,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isLoadingPermissionGroups, setIsLoadingPermissionGroups] =
    useState(false);
  const [permissionGroupsError, setPermissionGroupsError] = useState(null);
  const [investorPermissionGroups, setInvestorPermissionGroups] = useState({
    viewOnly: null,
    viewTrade: null,
  });
  const [customPermissions, setCustomPermissions] = useState({
    createUser: false,
    updateUserInfo: false,
    viewReports: false,
    manageTransactions: false,
    editSettings: false,
    deleteUser: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with member's current permissions
  useEffect(() => {
    if (open && member) {
      const currentGroup = member?.permissionGroup;
      
      if (currentGroup) {
        const groupName = String(currentGroup.groupName || "").toLowerCase();
        if (groupName.includes("view") && groupName.includes("only")) {
          setSelectedTemplate("view-only");
        } else if (
          groupName.includes("view") &&
          (groupName.includes("trade") || groupName.includes("and"))
        ) {
          setSelectedTemplate("view-trade");
        } else {
          setSelectedTemplate("custom");
        }
      } else {
        setSelectedTemplate("custom");
      }
    }
  }, [open, member]);

  // Fetch investor permission groups when modal opens
  useEffect(() => {
    const fetchInvestorPermissionGroups = async () => {
      if (!open) return;
      if (investorPermissionGroups.viewOnly || investorPermissionGroups.viewTrade)
        return;

      setIsLoadingPermissionGroups(true);
      setPermissionGroupsError(null);

      try {
        const url = `${Base_URL}/permissions/groups?groupRole=INVESTOR`;
        const response = await ApiCall(url, null, "get");
        const groups = Array.isArray(response?.data) ? response.data : [];

        const normalizeGroupName = (name) =>
          String(name || "")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");

        const viewOnlyGroup = groups.find((group) => {
          const normalized = normalizeGroupName(group.groupName);
          return normalized === "viewonly" || normalized === "readonly";
        });

        const viewTradeGroup = groups.find((group) => {
          const normalized = normalizeGroupName(group.groupName);
          return (
            normalized === "viewtrade" ||
            normalized === "viewandtrade" ||
            normalized === "tradeaccess"
          );
        });

        setInvestorPermissionGroups({
          viewOnly: viewOnlyGroup || null,
          viewTrade: viewTradeGroup || null,
        });
      } catch (error) {
        console.error("Error fetching investor permission groups:", error);
        setPermissionGroupsError("Unable to load permission templates.");
        setInvestorPermissionGroups({
          viewOnly: null,
          viewTrade: null,
        });
      } finally {
        setIsLoadingPermissionGroups(false);
      }
    };

    fetchInvestorPermissionGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleTemplateChange = (template) => {
    if (template === "view-only" && !investorPermissionGroups.viewOnly) return;
    if (template === "view-trade" && !investorPermissionGroups.viewTrade) return;

    setSelectedTemplate(template);

    if (template === "custom") {
      setCustomPermissions({
        createUser: false,
        updateUserInfo: false,
        viewReports: false,
        manageTransactions: false,
        editSettings: false,
        deleteUser: false,
      });
    }
  };

  const handleCustomPermissionToggle = (permission) => {
    setCustomPermissions((prev) => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  };

  const handleSubmit = async () => {
    if (!member || !household || !selectedTemplate || isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Determine the permission group ID based on selected template
      let permissionGroupId = null;

      if (selectedTemplate === "view-only") {
        permissionGroupId = investorPermissionGroups.viewOnly?.id || null;
      } else if (selectedTemplate === "view-trade") {
        permissionGroupId = investorPermissionGroups.viewTrade?.id || null;
      }
      // For custom, permissionGroupId remains null

      // Get all existing members and update the selected member's permissions
      const existingMembers = Array.isArray(household.members)
        ? household.members
        : [];

      const updatedMembers = existingMembers.map((m) => {
        // Update the selected member
        if (m.id === member.id) {
          return {
            userRoleId: m?.userRole?.id,
            permissionGroupId: permissionGroupId,
            isOwner: !!m?.isOwner,
          };
        }
        // Keep other members as is
        return {
          userRoleId: m?.userRole?.id,
          permissionGroupId: m?.permissionGroup?.id || null,
          isOwner: !!m?.isOwner,
        };
      });

      const body = {
        householdName: household.householdName,
        description: household.description || "",
        members: updatedMembers,
        invitationExpiryDays: 7,
        isActive:
          typeof household.isActive === "boolean" ? household.isActive : true,
      };

      const url = `${Base_URL}/households/${household.id}`;
      const response = await ApiCall(url, body, "put");
      const statusCode = response?.statusCode || response?.code;

      const ok =
        statusCode === 200 ||
        statusCode === 201 ||
        String(response?.status || "").toUpperCase() === "SUCCESS";

      if (!ok) {
        const errorMessage = response?.message ||
          "Unable to update member permissions. Please try again.";
        customFailAlert(errorMessage);
        if (onPermissionsUpdated) {
          onPermissionsUpdated({ success: false, message: errorMessage });
        }
        return;
      }

      if (onPermissionsUpdated) {
        onPermissionsUpdated({ success: true, message: "Member permissions updated successfully", data: response });
      }

      handleClose();
    } catch (error) {
      console.error("Error updating member permissions:", error);
      const errorMessage = "Something went wrong while updating permissions.";
      customFailAlert(errorMessage);
      if (onPermissionsUpdated) {
        onPermissionsUpdated({ success: false, message: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate("");
    setCustomPermissions({
      createUser: false,
      updateUserInfo: false,
      viewReports: false,
      manageTransactions: false,
      editSettings: false,
      deleteUser: false,
    });
    setPermissionGroupsError(null);
    onClose();
  };

  const memberName =
    member?.userInfo?.name ||
    `${member?.userInfo?.firstName || ""} ${member?.userInfo?.lastName || ""}`.trim() ||
    member?.userRole?.user?.email ||
    "Member";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "550px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#000", mb: 0.5 }}
            >
              Edit Permissions
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              You're editing permissions for {memberName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "#6B7280", mt: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Template Options */}
        <Box sx={{ display: "flex", gap: 2, mb: 4, position: "relative" }}>
          {permissionGroupsError && (
            <Typography
              sx={{
                position: "absolute",
                top: -24,
                left: 0,
                fontSize: "12px",
                color: "#DC2626",
              }}
            >
              {permissionGroupsError}
            </Typography>
          )}

          {/* View-Only */}
          <Box
            onClick={() => handleTemplateChange("view-only")}
            sx={{
              flex: 1,
              border:
                selectedTemplate === "view-only"
                  ? "2px solid #2563EB"
                  : "2px solid #E5E7EB",
              borderRadius: 3,
              p: 2.5,
              cursor:
                !isLoadingPermissionGroups &&
                !investorPermissionGroups.viewOnly
                  ? "not-allowed"
                  : "pointer",
              position: "relative",
              transition: "all 0.2s",
              opacity:
                !isLoadingPermissionGroups &&
                !investorPermissionGroups.viewOnly
                  ? 0.5
                  : 1,
              pointerEvents:
                !isLoadingPermissionGroups &&
                !investorPermissionGroups.viewOnly
                  ? "none"
                  : "auto",
              "&:hover": {
                borderColor: "#2563EB",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            <Radio
              checked={selectedTemplate === "view-only"}
              disabled={
                !investorPermissionGroups.viewOnly && !isLoadingPermissionGroups
              }
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: 0,
                color: "#D1D5DB",
                "&.Mui-checked": {
                  color: "#2563EB",
                },
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#000",
                textAlign: "center",
                mt: 1,
              }}
            >
              View-Only
            </Typography>
          </Box>

          {/* View & Trade */}
          <Box
            onClick={() => handleTemplateChange("view-trade")}
            sx={{
              flex: 1,
              border:
                selectedTemplate === "view-trade"
                  ? "2px solid #2563EB"
                  : "2px solid #E5E7EB",
              borderRadius: 3,
              p: 2.5,
              cursor:
                !isLoadingPermissionGroups &&
                !investorPermissionGroups.viewTrade
                  ? "not-allowed"
                  : "pointer",
              position: "relative",
              transition: "all 0.2s",
              opacity:
                !isLoadingPermissionGroups &&
                !investorPermissionGroups.viewTrade
                  ? 0.5
                  : 1,
              pointerEvents:
                !isLoadingPermissionGroups &&
                !investorPermissionGroups.viewTrade
                  ? "none"
                  : "auto",
              "&:hover": {
                borderColor: "#2563EB",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            <Radio
              checked={selectedTemplate === "view-trade"}
              disabled={
                !investorPermissionGroups.viewTrade && !isLoadingPermissionGroups
              }
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: 0,
                color: "#D1D5DB",
                "&.Mui-checked": {
                  color: "#2563EB",
                },
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#000",
                textAlign: "center",
                mt: 1,
              }}
            >
              View & Trade
            </Typography>
          </Box>

          {/* Custom */}
          <Box
            onClick={() => handleTemplateChange("custom")}
            sx={{
              flex: 1,
              border:
                selectedTemplate === "custom"
                  ? "2px solid #2563EB"
                  : "2px solid #E5E7EB",
              borderRadius: 3,
              p: 2.5,
              cursor: "pointer",
              position: "relative",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "#2563EB",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            <Radio
              checked={selectedTemplate === "custom"}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: 0,
                color: "#D1D5DB",
                "&.Mui-checked": {
                  color: "#2563EB",
                },
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#000",
                textAlign: "center",
                mt: 1,
              }}
            >
              Custom
            </Typography>
          </Box>
        </Box>

        {/* Loading State */}
        {isLoadingPermissionGroups && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <CircularProgress size={18} sx={{ color: "#2563EB" }} />
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Loading permission templates...
            </Typography>
          </Box>
        )}

        {/* View-Only Permissions */}
        {selectedTemplate === "view-only" &&
          investorPermissionGroups.viewOnly && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 3,
                border: "1px solid #E5E7EB",
                px: 3,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#111827",
                  mb: 1,
                }}
              >
                {investorPermissionGroups.viewOnly.groupName}
              </Typography>
              <List dense sx={{ maxHeight: 220, overflow: "auto" }}>
                {(investorPermissionGroups.viewOnly.apiNames || []).map(
                  (api) => (
                    <ListItem key={api} sx={{ py: 0.25 }}>
                      <CheckCircleIcon
                        sx={{ color: "#16A34A", fontSize: 20, mr: 1.5 }}
                      />
                      <ListItemText
                        primary={api}
                        primaryTypographyProps={{
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      />
                    </ListItem>
                  )
                )}
                {(!investorPermissionGroups.viewOnly.apiNames ||
                  investorPermissionGroups.viewOnly.apiNames.length === 0) && (
                  <ListItem>
                    <ListItemText
                      primary="No APIs configured for this group."
                      primaryTypographyProps={{
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

        {/* View & Trade Permissions */}
        {selectedTemplate === "view-trade" &&
          investorPermissionGroups.viewTrade && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 3,
                border: "1px solid #E5E7EB",
                px: 3,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#111827",
                  mb: 1,
                }}
              >
                {investorPermissionGroups.viewTrade.groupName}
              </Typography>
              <List dense sx={{ maxHeight: 220, overflow: "auto" }}>
                {(investorPermissionGroups.viewTrade.apiNames || []).map(
                  (api) => (
                    <ListItem key={api} sx={{ py: 0.25 }}>
                      <CheckCircleIcon
                        sx={{ color: "#16A34A", fontSize: 20, mr: 1.5 }}
                      />
                      <ListItemText
                        primary={api}
                        primaryTypographyProps={{
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      />
                    </ListItem>
                  )
                )}
                {(!investorPermissionGroups.viewTrade.apiNames ||
                  investorPermissionGroups.viewTrade.apiNames.length === 0) && (
                  <ListItem>
                    <ListItemText
                      primary="No APIs configured for this group."
                      primaryTypographyProps={{
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

        {/* Custom Permissions */}
        {selectedTemplate === "custom" && (
          <Box>
            {[
              { key: "createUser", label: "Create User" },
              { key: "updateUserInfo", label: "Update User Info" },
              { key: "viewReports", label: "View Reports" },
              { key: "manageTransactions", label: "Manage Transactions" },
              { key: "editSettings", label: "Edit Settings" },
              { key: "deleteUser", label: "Delete User" },
            ].map((item) => (
              <FormControlLabel
                key={item.key}
                control={
                  <Checkbox
                    checked={customPermissions[item.key]}
                    onChange={() => handleCustomPermissionToggle(item.key)}
                    sx={{
                      color: "#D1D5DB",
                      "&.Mui-checked": {
                        color: "#10B981",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                    {item.label}
                  </Typography>
                }
                sx={{ mb: 1.5, width: "100%" }}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            borderColor: "#D1D5DB",
            color: "#374151",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedTemplate || isSubmitting}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            backgroundColor: selectedTemplate && !isSubmitting ? "#2563EB" : "#E5E7EB",
            color: selectedTemplate && !isSubmitting ? "#FFFFFF" : "#9CA3AF",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: selectedTemplate && !isSubmitting ? "#1D4ED8" : "#E5E7EB",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {isSubmitting ? "Updating..." : "Update Permissions"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMemberPermissionsModal;

